import { Layout, Card, Typography, Row, Col, Button, Space, Spin } from "antd";

import React, { forwardRef, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import { formatDateText, formatHm } from "@/utils/date";
import settingIcon from "@/assets/images/icons/setting.png";
import employeesIcon from "@/assets/images/icons/employees.png";
import wallet2Icon from "@/assets/images/icons/wallet_2.png";
import bankIcon from "@/assets/images/icons/bankicon.png";
import timeIcon from "@/assets/images/icons/time.png";
import AttendanceModal from "./detailModal";
import dayjs from "dayjs";
const MainTable = forwardRef((_props, _ref) => {
  const { Header, Content } = Layout;
  const { Title } = Typography;
  const attendanceModalRef = useRef(null);
  const location = useLocation();
  const token = new URLSearchParams(location?.search || "").get("token");
  const legacyStorageKey = token ? `managerWorkTimeDetailRecord:${token}` : "";
  const storageKey = "managerWorkTimeDetailRecord";

  let passedRecord = location?.state?.record;
  const readStorageRecord = (key) => {
    if (!key) return null;

    try {
      const v = localStorage.getItem(key);
      if (v) return JSON.parse(v);
    } catch (_e) {
      // ignore
    }

    try {
      const v = sessionStorage.getItem(key);
      if (v) return JSON.parse(v);
    } catch (_e) {
      // ignore
    }

    return null;
  };

  if (!passedRecord) {
    passedRecord =
      readStorageRecord(legacyStorageKey) || readStorageRecord(storageKey);
  }

  const accountId = passedRecord?.accountId;

  const [columns, setColumns] = useState([]);
  const [tableList, setTableList] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [employmentTimeAllowanceList, setEmploymentTimeAllowanceList] =
    useState([]);
  const [supervisorList, setSupervisorList] = useState([]);
  const [propertyMoveInList, setPropertyMoveInList] = useState([]);
  const [unitIdList, setUnitIdList] = useState([]);

  const controllersRef = useRef([]);
  const todayRef = useRef("");
  const employeeIdRef = useRef(passedRecord?.id || null);
  const createController = () => {
    const controller = new AbortController();
    controllersRef.current.push(controller);
    return controller;
  };

  const cancelAllRequests = () => {
    controllersRef.current.forEach((c) => c?.abort());
    controllersRef.current = [];
  };

  const formatText = (value) => {
    if (value === 0) return "0";
    // if (value === true) return "Yes";
    // if (value === false) return "No";
    if (value === null || value === undefined) return "";
    const v = String(value).trim();
    return v ? v : "";
  };

  const formatBoolNumber = (value) => {
    if (value === 1 || value === "1" || value === true) return "Yes";
    if (value === 0 || value === "0" || value === false) return "No";
    return formatText(value);
  };

  const formatEmpStatus = (value) => {
    const v = Number(value);
    if (v === 1) return "Full Time";
    if (v === 2) return "Part Time";
    return "";
  };

  const resolvePayStatus = (payStatus, employmentStatus) => {
    const emp = Number(employmentStatus);
    if (emp === 2) return 2;
    const p = Number(payStatus);
    return p === 1 || p === 2 ? p : null;
  };

  const formatPayStatus = (payStatus, employmentStatus) => {
    const v = resolvePayStatus(payStatus, employmentStatus);
    if (v === 1) return "Salary";
    if (v === 2) return "Hourly";
    return "";
  };

  const formatPayRateStatus = (payRate, payStatus, employmentStatus) => {
    if (payRate === null || payRate === undefined || payRate === "") return "";

    const v = resolvePayStatus(payStatus, employmentStatus);
    const suffix = v === 1 ? "USD/Month" : "USD/Hour";

    const numberText = commonFn.inputAmountFormat(payRate, false, true);
    if (!numberText) return "";
    return `$${numberText} ${suffix}`;
  };

  const getEmploymentTimeAllowanceList = async () => {
    const controller = createController();
    const response = await http.get(api.employeesTimeAllowanceDropdown, {
      params: { type: "RENT_CREDIT" },
      signal: controller.signal,
    });
    if (response?.code === "1") {
      setEmploymentTimeAllowanceList(response.data || []);
    }
  };

  const propIdFn = async (content = "", type = "") => {
    const controller = createController();
    const response = await http.get(api.propSearch, {
      params: { content },
      signal: controller.signal,
    });
    if (response?.code === "1") {
      if (type === "propertyMoveIn") {
        setPropertyMoveInList(response?.data || []);
      } else {
        setPropertyMoveInList(response?.data || []);
      }
    }
  };

  const unitIdFun = async (propId, content = "") => {
    setUnitIdList([]);
    if (!propId) return;
    const controller = createController();
    const response = await http.get(api.unitSearch, {
      params: { propId, content },
      signal: controller.signal,
    });
    if (response?.code === "1") {
      setUnitIdList(response?.data || []);
    }
  };

  const getSupervisorList = async () => {
    const controller = createController();
    const response = await http.get(api.getSupervisorNameDict, {
      signal: controller.signal,
    });
    if (response?.code === "1") {
      setSupervisorList(response?.data || []);
    }
  };

  const getWorkTimeDetailFn = async () => {
    if (!accountId) return null;

    const controller = createController();
    const response = await http.get(api.getManagerWorkTimeById, {
      params: { accountId },
      signal: controller.signal,
    });

    if (!response) return null;

    if (response.code !== "1") {
      commonFn.modalFailedFn(response);
      return null;
    }

    const data = response.data || null;

    todayRef.current = data?.today || "";

    const list = Array.isArray(data?.managerDailyRecordDetailRowVoList)
      ? data.managerDailyRecordDetailRowVoList
      : [];
    setTableList(
      list.map((item, idx) => ({
        ...item,
        _key: item?.weekLabel || `${item?.weekType || "week"}_${idx}`,
      })),
    );

    const baseColumns = [
      {
        title: "Over-Time Alert",
        dataIndex: "overTimeAlert",
        align: "center",
        width: 140,
        onCell: () => ({ style: { padding: 4 } }),
        render: (value) => (
          <div style={{ fontSize: 13, color: "#2f3a4a", fontWeight: 500 }}>
            {formatHm(value)}
          </div>
        ),
      },
      {
        title: "Total Hours",
        key: "totalHours",
        align: "center",
        width: 140,
        onCell: () => ({ style: { padding: 4 } }),
        render: (_v, record) => (
          <div style={{ fontSize: 13, color: "#2f3a4a", fontWeight: 500 }}>
            {`${Number(record?.totalHours) || 0} Hrs; ${
              Number(record?.totalMinutes) || 0
            } Min`}
          </div>
        ),
      },
    ];

    const weekColumns = DAY_ORDER.map((day) => weekColumnFn(day));
    setColumns([...baseColumns, ...weekColumns]);

    const nextEmployeeId = data?.employeesInfo?.id || data?.id || null;
    if (!employeeIdRef.current && nextEmployeeId) {
      employeeIdRef.current = nextEmployeeId;
    }
    return nextEmployeeId;
  };

  const getEmployeesInformationDetailFn = async (id) => {
    if (!id) return null;

    const controller = createController();
    const response = await http.get(api.employeesInformationDetailView, {
      params: { id },
      signal: controller.signal,
    });

    if (!response) return null;

    if (response.code !== "1") {
      commonFn.modalFailedFn(response);
      return null;
    }
    const raw = response.data || null;
    if (!raw) {
      setDetailData(null);
      return null;
    }

    const propList = Array.isArray(raw?.propList) ? raw.propList : [];
    const groupList = Array.isArray(raw?.groupList) ? raw.groupList : [];

    const propText = propList
      .map((item) => item?.prop || "")
      .filter(Boolean)
      .join(", ");

    const groupText = groupList
      .map((item) => item?.groupCode || "")
      .filter(Boolean)
      .join(", ");

    const moveInPropList = Array.isArray(raw?.moveInProp) ? raw.moveInProp : [];
    const moveInUnitList = Array.isArray(raw?.moveInUnit) ? raw.moveInUnit : [];

    const propertyMoveInItem = moveInPropList.find(
      (item) =>
        String(item?.propId ?? item?.id ?? "") ===
        String(raw?.propertyMoveIn ?? ""),
    );
    const unitLiveInItem = moveInUnitList.find(
      (item) =>
        String(item?.unitId ?? item?.id ?? "") ===
        String(raw?.unitLiveIn ?? ""),
    );

    const propertyMoveInText =
      propertyMoveInItem?.propName ||
      propertyMoveInItem?.prop ||
      propertyMoveInItem?.street ||
      "";

    const unitLiveInText =
      unitLiveInItem?.unitName ||
      unitLiveInItem?.unitNo ||
      unitLiveInItem?.unit ||
      "";

    const temp = {
      ...raw,
      propIdList: propList.map((item) => item?.propId).filter(Boolean),
      groupIdList: groupList.map((item) => item?.groupId).filter(Boolean),
      propText,
      groupText,
      propertyMoveInText,
      unitLiveInText,
    };

    setDetailData(temp);
    if (raw?.propertyMoveIn) {
      await propIdFn("", "propertyMoveIn");
      await unitIdFun(raw?.propertyMoveIn);
    }
    return temp;
  };

  const loadAllFn = async () => {
    if (!accountId) return;

    setDetailLoading(true);
    try {
      if (!employmentTimeAllowanceList.length) {
        await getEmploymentTimeAllowanceList();
      }
      if (!supervisorList.length) {
        await getSupervisorList();
      }
      const workEmployeeId = await getWorkTimeDetailFn();
      const idToUse = employeeIdRef.current || workEmployeeId;
      if (idToUse) {
        await getEmployeesInformationDetailFn(idToUse);
      } else {
        setDetailData(null);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const LEGEND_COLORS = {
    personal: "#CE9EE2",
    annual: "#ADD5C6",
    sick: "#F5B5B5",
    statutory: "#FAC6AB",
  };
  const ZERO_HOUR_BG = "#ffffff";
  const LOW_HOUR_BG = "#DBE8FF";
  const HIGH_HOUR_BG = "#98B5F5";

  const resolveDayTheme = (timeObj = {}) => {
    const marks = Array.isArray(timeObj?.marks) ? timeObj.marks : [];

    // 法定假日
    if (marks.includes(1)) {
      return {
        bg: LEGEND_COLORS.statutory,
        note: timeObj?.holidayName || "Holiday",
      };
    }

    // 0 工时
    if (marks.includes(4)) {
      return { bg: ZERO_HOUR_BG, note: "" };
    }

    // 高工时
    if (marks.includes(2)) {
      return { bg: HIGH_HOUR_BG, note: "" };
    }

    // （如果恢复低工时）
    // if (marks.includes(3)) {
    //   return { bg: LOW_HOUR_BG, note: "" };
    // }
    if (!marks.length) {
      return { bg: LOW_HOUR_BG, note: "" };
    }

    // 默认（没有任何标记）
    return { bg: ZERO_HOUR_BG, note: "" };
  };

  //  星期列
  const DAY_ORDER = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

const weekColumnFn = (day) => ({
  title: day.charAt(0).toUpperCase() + day.slice(1),
  dataIndex: ["weekMap", day],
  align: "center",
  width: 150,
  onCell: () => ({
    style: { padding: 4 },
  }),
  render: (timeObj = {}) => {
    const { hours = 0, minutes = 0 } = timeObj;
    const { bg } = resolveDayTheme(timeObj);
    const isToday = !!todayRef.current && timeObj?.date === todayRef.current;
    const isZeroBg = bg === ZERO_HOUR_BG;
    const textColor = "#373D4E";

    // 判断是否为未来日期（今天之后的日期）
    const isFutureDate = timeObj?.date && dayjs(timeObj.date).isAfter(dayjs(), "day");
    const isClickable = !isFutureDate && timeObj?.date;

    const handleDateClick = () => {
      if (isClickable && accountId) {
        attendanceModalRef.current?.open({
          accountId: accountId,
          date: timeObj?.date,
        });
      }
    };

    // 未来日期的样式
    const futureDateStyle = isFutureDate ? {
      opacity: 0.5,
      cursor: "not-allowed",
    } : {};

    return (
      <div
        style={{
          background: bg,
          borderRadius: 4,
          width: "100%",
          height: 72,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
          border: isToday
            ? "1px solid #2563EB"
            : isZeroBg
              ? "1px solid #E2E8F0"
              : "1px solid transparent",
          boxShadow: isToday
            ? "4px 4px 4px 4px rgba(105, 147, 238, 0.2)"
            : "none",
          boxSizing: "border-box",
          cursor: isClickable ? "pointer" : isFutureDate ? "not-allowed" : "default",
          transition: isClickable ? "all 0.2s" : "none",
          ...futureDateStyle,
        }}
        onClick={handleDateClick}
        onMouseEnter={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = "scale(1.02)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(37, 99, 235, 0.15)";
          }
        }}
        onMouseLeave={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "none";
          }
        }}
      >
        <div style={{ 
          fontSize: 12, 
          color: textColor, 
          marginBottom: 2,
          ...(isFutureDate && { color: "#999" }),
        }}>
          {formatDateText(timeObj?.date)}
        </div>

        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: textColor,
            lineHeight: 1.1,
            ...(isFutureDate && { color: "#999" }),
          }}
        >
          {hours} Hrs / {minutes} Min
        </div>
      </div>
    );
  },
});
  const exportCsv = () => {
    // 暂时没有接口
  };

  const handleRefresh = () => {
    loadAllFn();
  };

  useEffect(() => {
    loadAllFn();

    return () => {
      cancelAllRequests();
    };
  }, []);

  const detail = detailData || {};
  const displayName =
    detail?.accountName || passedRecord?.accountName || "Manager Work Time";

  const supervisorText = (() => {
    const id = detail?.supervisorAccountId;
    if (id === null || id === undefined || id === "") return "";

    const list = Array.isArray(supervisorList) ? supervisorList : [];
    const match = list.find((item) => {
      const value = item?.accountId ?? item?.id ?? item?.value;
      return String(value ?? "") === String(id);
    });

    return (
      match?.fullName ||
      match?.accountName ||
      match?.name ||
      match?.label ||
      match?.title ||
      match?.titleName ||
      formatText(id)
    );
  })();

  const rentCreditText = (() => {
    const list = employmentTimeAllowanceList || [];
    const match = list.find(
      (item) => String(item?.id) === String(detail?.rentCreditId),
    );
    return match?.titleName || match?.name || formatText(detail?.rentCredit);
  })();

  const propertyMoveInText = (() => {
    if (!detail?.propertyMoveIn) return formatText(detail?.propertyMoveInText);
    const list = propertyMoveInList || [];
    const match = list.find(
      (item) =>
        String(item?.propId ?? item?.id ?? "") ===
        String(detail?.propertyMoveIn),
    );
    return (
      // match?.propName ||
      match?.prop ||
      // match?.street ||
      formatText(detail?.propertyMoveInText)
    );
  })();

  const unitLiveInText = (() => {
    if (!detail?.unitLiveIn) return formatText(detail?.unitLiveInText);
    const list = unitIdList || [];
    const match = list.find(
      (item) =>
        String(item?.unitId ?? item?.id ?? "") === String(detail?.unitLiveIn),
    );
    return (
      match?.unitName ||
      match?.unitNo ||
      match?.unit ||
      formatText(detail?.unitLiveInText)
    );
  })();

  const renderInfoRows = (items = []) => {
    return items.map((item) => (
      <div
        key={item.label}
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          padding: "6px 0",
          // borderBottom:"1px solid #E2E8F0"
        }}
      >
        <div style={{ color: "#64748B", flex: "0 0 auto" }}>{item.label}</div>
        <div style={{ color: "#1f2a37", fontWeight: 500, textAlign: "left" }}>
          {item.value}
        </div>
      </div>
    ));
  };

  const infoCardProps = {
    size: "small",
    bodyStyle: { paddingTop: 8, paddingBottom: 8 },
    style: { border: "1px solid #E2E8F0", borderRadius: 12 },
  };

  const cardTitle = (icon, text) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 16,
        fontWeight: 600,
      }}
    >
      <img src={icon} alt="" style={{ width: 16, height: 16 }} />
      <span>{text}</span>
    </div>
  );

  const rentalInfoItems = [
    {
      label: "Is Move In",
      value: formatBoolNumber(detail?.isMoveIn),
    },
  ];

  if (Number(detail?.isMoveIn) === 1) {
    rentalInfoItems.push(
      {
        label: "Property MoveIn",
        value: formatText(propertyMoveInText),
      },
      {
        label: "Unit Live In",
        value: formatText(unitLiveInText),
      },
      {
        label: "Rent Credit",
        value: formatText(rentCreditText),
      },
    );
  }

  return (
    <div className="dashboard-layout-payroll">
      <Header className="dashboard-header">
        <div className="dashboard-header-left">
          <Title level={4}>Manager Work Time Detail</Title>
        </div>
      </Header>

      <Content style={{ marginTop: 10 }}>
        <Card
          styles={{
            body: { padding: 0 },
          }}
        >
          <Spin spinning={detailLoading}>
            <div
              style={{
                display: "flex",
                minHeight: "calc(100vh - 210px)",
                gap: 0,
              }}
            >
              <div
                style={{
                  flex: "1 1 40%",
                  minWidth: 0,
                  padding: 20,
                  borderRight: "1px solid #E2E8F0",
                  boxSizing: "border-box",
                }}
              >
                <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                  {displayName}
                </Title>

                <Row gutter={16}>
                  <Col span={12}>
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: "100%" }}
                    >
                      <Card
                        title={cardTitle(settingIcon, "Account Info")}
                        {...infoCardProps}
                      >
                        {renderInfoRows([
                          {
                            label: "Exist Account",
                            value: formatBoolNumber(detail?.active),
                          },
                          {
                            label: "Email",
                            value: formatText(detail?.email),
                          },
                          {
                            label: "First Name",
                            value: formatText(detail?.firstName),
                          },
                          {
                            label: "Middle Name",
                            value: formatText(detail?.middleName),
                          },
                          {
                            label: "Last Name",
                            value: formatText(detail?.lastName),
                          },
                          {
                            label: "Cell Phone",
                            value: formatText(detail?.cellPhone),
                          },
                          {
                            label: "Note",
                            value: formatText(detail?.note),
                          },
                          {
                            label: "Prop",
                            value: formatText(detail?.propText),
                          },
                          {
                            label: "Group",
                            value: formatText(detail?.groupText),
                          },
                        ])}
                      </Card>

                      <Card
                        title={cardTitle(employeesIcon, "Employee Info")}
                        {...infoCardProps}
                      >
                        {renderInfoRows([
                          {
                            label: "Employee Code",
                            value: formatText(detail?.employeeCode),
                          },
                          {
                            label: "Supervisor Account",
                            value: formatText(supervisorText),
                          },
                          {
                            label: "Ssn",
                            value: detail?.ssn,
                          },
                          {
                            label: "Driver License",
                            value: formatText(detail?.driverLicense),
                          },
                          {
                            label: "Company Work Under",
                            value: formatText(detail?.companyWorkUnder),
                          },
                          {
                            label: "Office Location",
                            value: formatText(detail?.officeLocation),
                          },
                          {
                            label: "Hire Date",
                            value: formatDateText(detail?.hireDate, ""),
                          },
                          {
                            label: "Employment Title",
                            value: formatText(detail?.employmentTitleName),
                          },
                        ])}
                      </Card>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: "100%" }}
                    >
                      <Card
                        title={cardTitle(wallet2Icon, "Employment Info")}
                        {...infoCardProps}
                      >
                        {renderInfoRows([
                          {
                            label: "Employment Status",
                            value: formatEmpStatus(detail?.employmentStatus),
                          },
                          {
                            label: "Pay Status",
                            value: formatPayStatus(
                              detail?.payStatus,
                              detail?.employmentStatus,
                            ),
                          },
                          {
                            label: "Pay Rate",
                            value: formatPayRateStatus(
                              detail?.payRate,
                              detail?.payStatus,
                              detail?.employmentStatus,
                            ),
                          },
                        ])}
                      </Card>

                      <Card
                        title={cardTitle(bankIcon, "Rental Info")}
                        {...infoCardProps}
                      >
                        {renderInfoRows(rentalInfoItems)}
                      </Card>

                      <Card
                        title={cardTitle(timeIcon, "Other Info")}
                        {...infoCardProps}
                      >
                        {renderInfoRows([
                          {
                            label: "Create By",
                            value: formatText(detail?.createByName),
                          },
                          {
                            label: "Create Date",
                            value: formatText(detail?.createTime),
                          },
                          {
                            label: "Modifier",
                            value: formatText(detail?.updateByName),
                          },
                          {
                            label: "Modified Date",
                            value: formatText(detail?.updateTime),
                          },
                        ])}
                      </Card>
                    </Space>
                  </Col>
                </Row>
              </div>

              <div
                style={{
                  flex: "1 1 60%",
                  padding: 20,
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      flexWrap: "wrap",
                      fontSize: 12,
                    }}
                  >
                    <span>
                      <span style={{ color: LEGEND_COLORS.personal }}>■</span>{" "}
                      Personal Leave
                    </span>
                    <span>
                      <span style={{ color: LEGEND_COLORS.annual }}>■</span>{" "}
                      Vacation
                    </span>
                    <span>
                      <span style={{ color: LEGEND_COLORS.sick }}>■</span> Sick
                      Sick
                    </span>
                    <span>
                      <span style={{ color: LEGEND_COLORS.statutory }}>■</span>{" "}
                      Holiday
                    </span>
                    <span>
                      <span style={{ color: LOW_HOUR_BG, marginRight: 6 }}>
                        ■
                      </span>
                      &lt;8 Hrs
                    </span>
                    <span>
                      <span style={{ color: HIGH_HOUR_BG, marginRight: 6 }}>
                        ■
                      </span>
                      &gt;8 Hrs
                    </span>
                  </div>

                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRefresh}
                      loading={detailLoading}
                    >
                      Refresh
                    </Button>
                    {/* <Button
                      icon={<DownloadOutlined />}
                      onClick={exportCsv}
                      disabled={!tableList?.length}
                    >
                      Export
                    </Button> */}
                  </Space>
                </div>

                <EnhancedTable
                  rowKey={(record, index) => record?._key || `${index}`}
                  columns={columns}
                  dataSource={tableList}
                  loading={false}
                  pagination={false}
                  rowSelection={undefined}
                  scroll={{
                    x: "max-content",
                    y: "calc(100vh - 350px)",
                  }}
                />
              </div>
            </div>
          </Spin>
        </Card>

        <AttendanceModal
          ref={attendanceModalRef}
          onSuccess={() => {
            loadAllFn();
          }}
        />
      </Content>
    </div>
  );
});

export default MainTable;
